<template>
  <v-expansion-panels
    v-if="partners.length"
    :model-value="expansionPanelValue"
    elevation="4"
    :class="['partners-carousel', { 'partners-carousel--compact': isCompact }]"
    @update:model-value="onPanelChange"
  >
    <v-expansion-panel value="partners">
      <!-- The panel sits at the bottom of the screen and opens upwards, so the chevrons are swapped -->
      <v-expansion-panel-title
        static
        expand-icon="mdi-chevron-up"
        collapse-icon="mdi-chevron-down"
        :hide-actions="loadingPreference"
      >
        <span class="partners-title">
          <v-icon size="small" color="primary" class="mr-2">mdi-handshake</v-icon>
          Merci à nos partenaires
        </span>
        <template v-if="loadingPreference" #actions>
          <v-progress-circular indeterminate size="16" width="2" />
        </template>
      </v-expansion-panel-title>
      <v-expansion-panel-text eager>
        <div ref="partnersViewport" class="partners-viewport">
          <div v-if="!shouldUseCarousel" class="partners-row" :style="partnerRowStyle">
            <a
              v-for="(partner, i) in partners"
              :key="partner.id || i"
              :href="partner.url"
              target="_blank"
              rel="noopener noreferrer"
              class="partner-link"
              :title="partner.name"
              @click="handlePartnerClick(partner.id)"
            >
              <div class="partner-logo-wrap">
                <v-img :src="partner.image" :alt="partner.name" class="partner-logo" />
                <span v-if="partner.isPaying" class="partner-paying-badge" role="img" aria-label="Partenaire payant">
                  <v-icon size="x-small" color="on-gold">mdi-crown</v-icon>
                </span>
              </div>
            </a>
          </div>

          <v-carousel
            v-else
            v-model="carouselIndex"
            :cycle="true"
            :touch="false"
            hide-delimiters
            interval="6000"
            :height="isCompact ? 64 : 100"
            :show-arrows="false"
          >
            <v-carousel-item
              v-for="(slidePartners, slideIndex) in partnerSlides"
              :key="slideIndex"
              class="partner-slide"
            >
              <div class="partners-row" :style="partnerRowStyle">
                <a
                  v-for="(partner, partnerIndex) in slidePartners"
                  :key="partner.id || `${slideIndex}-${partnerIndex}`"
                  :href="partner.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="partner-link"
                  :title="partner.name"
                  @click="handlePartnerClick(partner.id)"
                >
                  <div class="partner-logo-wrap">
                    <v-img :src="partner.image" :alt="partner.name" class="partner-logo" />
                    <span v-if="partner.isPaying" class="partner-paying-badge" role="img" aria-label="Partenaire payant">
                      <v-icon size="x-small" color="on-gold">mdi-crown</v-icon>
                    </span>
                  </div>
                </a>
              </div>
            </v-carousel-item>
          </v-carousel>

          <!-- Compact (short screens): the arrows sit on each side of the logos instead of under them -->
          <div
            v-if="shouldUseCarousel && partnerSlides.length > 1"
            :class="['carousel-controls', { 'carousel-controls--side': isCompact }]"
          >
            <v-btn icon size="small" class="carousel-btn" @click.stop="goToPreviousSlide" aria-label="Partenaires précédents">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="carousel-indicator">{{ carouselIndex + 1 }} / {{ partnerSlides.length }}</span>
            <v-btn icon size="small" class="carousel-btn" @click.stop="goToNextSlide" aria-label="Partenaires suivants">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
  import { mapState, mapActions } from "vuex";
  import firebase from "firebase/compat/app";
  import "firebase/compat/auth";
  import { UserService } from "@/store/modules/user";
  import { MOCK_PARTNERS } from "@/components/pages/river/components/partnersMock";

  const partnerCarouselPreferenceCache = new Map();
  // Below this height the strip is compact (see isCompact)
  const COMPACT_QUERY = "(max-height: 900px)";

  export default {
    name: "PartnersCaroussel",
    props: {
      isMember: {
        type: Boolean,
        default: false,
      },
      isAdmin: {
        type: Boolean,
        default: false,
      },
      isAuthResolved: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        showPartners: false,
        loadingPreference: false,
        hasResolvedInitialPreference: false,
        partnersViewportWidth: 0,
        partnerSlotGap: 12,
        // Short screens (laptops, phones): a thinner strip so it doesn't cover the page content
        isCompact: false,
        carouselIndex: 0,
        isAnimating: false,
      };
    },
    computed: {
      ...mapState({
        partners: (state) => {
          const today = new Date().toISOString().slice(0, 10);
          const real = state.PartnersService.partners.filter((p) => p.active && (!p.expiresAt || p.expiresAt >= today));
          if (process.env.NODE_ENV === "production") {
            return real;
          }
          const mockNeeded = MOCK_PARTNERS.filter(
            (m) => m.active && (!m.expiresAt || m.expiresAt >= today) && !real.some((p) => p.id === m.id),
          );
          return [...real, ...mockNeeded];
        },
      }),
      canUsePremiumPreference() {
        return this.isMember || this.isAdmin;
      },
      shouldRenderExpandedContent() {
        if (!this.isAuthResolved) {
          return false;
        }

        if (!this.canUsePremiumPreference) {
          return this.showPartners;
        }

        if (!this.hasResolvedInitialPreference) {
          return false;
        }

        return this.showPartners;
      },
      expansionPanelValue() {
        return this.shouldRenderExpandedContent ? "partners" : null;
      },
      // Logo tiles keep their 3.5:1 shape: 300 x 84 px, or 200 x 56 px when compact
      partnerSlotWidth() {
        return this.isCompact ? 200 : 300;
      },
      partnersPerSlide() {
        const fitCount = Math.floor(
          (this.partnersViewportWidth + this.partnerSlotGap) / (this.partnerSlotWidth + this.partnerSlotGap),
        );
        return Math.max(1, fitCount);
      },
      partnerRowStyle() {
        return {
          "--partner-slot-width": `${this.partnerSlotWidth}px`,
        };
      },
      shouldUseCarousel() {
        return this.partners.length > this.partnersPerSlide;
      },
      partnerSlides() {
        if (!this.partners.length) {
          return [];
        }

        if (!this.shouldUseCarousel) {
          return [this.partners];
        }

        const slides = [];
        for (let i = 0; i < this.partners.length; i += this.partnersPerSlide) {
          slides.push(this.partners.slice(i, i + this.partnersPerSlide));
        }
        return slides;
      },
    },
    created() {
      this.fetchPartners(); // Fetch partners when the component is created
      this.applyInitialVisibilityPreference();
    },
    mounted() {
      this.updateFooterHeight();
      this.updatePartnersViewportWidth();
      if (window.ResizeObserver) {
        this._ro = new ResizeObserver(() => {
          // Resize the layout on the next frame: doing it inside the observer callback triggers
          // "ResizeObserver loop completed with undelivered notifications".
          cancelAnimationFrame(this._roFrame);
          this._roFrame = requestAnimationFrame(() => {
            this.updateFooterHeight();
            this.updatePartnersViewportWidth();
          });
        });
        const footerEl = document.querySelector(".footer-container");
        const observeTarget =
          footerEl && footerEl.nodeType === 1 ? footerEl : this.$el && this.$el.nodeType === 1 ? this.$el : null;
        try {
          if (observeTarget && this._ro && this._ro.observe) this._ro.observe(observeTarget);
        } catch (e) {
          console.warn("ResizeObserver.observe failed:", e && e.message);
        }
      }
      window.addEventListener("resize", this.updateFooterHeight);
      window.addEventListener("resize", this.updatePartnersViewportWidth);
      if (window.matchMedia) {
        this._compactQuery = window.matchMedia(COMPACT_QUERY);
        this.isCompact = this._compactQuery.matches;
        this._compactQuery.addEventListener("change", this.onCompactChange);
      }
    },
    beforeUnmount() {
      if (this._ro && this._ro.disconnect) this._ro.disconnect();
      cancelAnimationFrame(this._roFrame);
      window.removeEventListener("resize", this.updateFooterHeight);
      window.removeEventListener("resize", this.updatePartnersViewportWidth);
      if (this._compactQuery) this._compactQuery.removeEventListener("change", this.onCompactChange);
      document.documentElement.style.setProperty("--footer-height", "0px");
    },
    methods: {
      async applyInitialVisibilityPreference() {
        if (this.loadingPreference) {
          return;
        }

        if (!this.isAuthResolved) {
          this.showPartners = false;
          this.hasResolvedInitialPreference = false;
          return;
        }

        if (!this.canUsePremiumPreference) {
          this.showPartners = true;
          this.hasResolvedInitialPreference = true;
          return;
        }

        const user = firebase.auth().currentUser;
        if (!user || !user.uid) {
          this.showPartners = true;
          this.hasResolvedInitialPreference = true;
          return;
        }

        if (partnerCarouselPreferenceCache.has(user.uid)) {
          this.showPartners = partnerCarouselPreferenceCache.get(user.uid);
          this.hasResolvedInitialPreference = true;
          return;
        }

        this.hasResolvedInitialPreference = false;
        this.showPartners = false;
        this.loadingPreference = true;
        try {
          const preferences = await UserService.actions.getUserUiPreferencesByUid(user.uid);
          this.showPartners = Boolean(preferences.partnerCarouselExpandedByDefault);
          partnerCarouselPreferenceCache.set(user.uid, this.showPartners);
        } catch (error) {
          console.error("Error loading partner carousel preference:", error);
          this.showPartners = true;
          partnerCarouselPreferenceCache.set(user.uid, true);
        } finally {
          this.loadingPreference = false;
          this.hasResolvedInitialPreference = true;
          this.$nextTick(() => {
            this.updateFooterHeight();
          });
        }
      },
      updateFooterHeight() {
        if (this.isAnimating) {
          return;
        }
        this.$nextTick(() => {
          const footerEl = document.querySelector(".footer-container");
          let height = 0;
          if (footerEl) {
            height = footerEl.offsetHeight;
          } else if (this.$el) {
            height = this.$el.offsetHeight;
          }
          document.documentElement.style.setProperty("--footer-height", height + "px");
        });
      },
      updatePartnersViewportWidth() {
        this.$nextTick(() => {
          const viewport = this.$refs.partnersViewport;
          if (!viewport || !viewport.clientWidth) {
            this.partnersViewportWidth = 0;
            return;
          }
          // Without the side padding that holds the arrows in compact mode
          const style = window.getComputedStyle(viewport);
          this.partnersViewportWidth =
            viewport.clientWidth - parseFloat(style.paddingLeft || 0) - parseFloat(style.paddingRight || 0);
        });
      },
      onCompactChange(event) {
        this.isCompact = event.matches;
        this.$nextTick(() => {
          this.updatePartnersViewportWidth();
          this.updateFooterHeight();
        });
      },
      onPanelChange(value) {
        this.showPartners = value === "partners";
        // Block footer/layout updates for the duration of the panel transition
        // so the animation is not interrupted by reflows.
        this.isAnimating = true;
        clearTimeout(this._animationTimer);
        this._animationTimer = setTimeout(() => {
          this.isAnimating = false;
          this.updateFooterHeight();
          this.updatePartnersViewportWidth();
        }, 350);
      },
      goToPreviousSlide() {
        if (!this.partnerSlides.length) {
          return;
        }
        this.carouselIndex = (this.carouselIndex - 1 + this.partnerSlides.length) % this.partnerSlides.length;
      },
      goToNextSlide() {
        if (!this.partnerSlides.length) {
          return;
        }
        this.carouselIndex = (this.carouselIndex + 1) % this.partnerSlides.length;
      },
      ...mapActions("PartnersService", ["fetchPartners"]),
      async handlePartnerClick(partnerId) {
        try {
          const user = firebase.auth().currentUser;

          const userInfo = user
            ? {
                id: user.uid,
                name: user.displayName || "Anonymous",
                email: user.email,
              }
            : {
                id: "anonymous",
                name: "Anonymous",
                email: null,
              };

          // Explicitly dispatch the action
          await this.$store.dispatch("PartnersService/logPartnerClick", { partnerId, userInfo });
        } catch (error) {
          console.error("Error in handlePartnerClick:", error.message); // Log the error to the console
        }
      },
    },
    watch: {
      isAuthResolved: {
        immediate: true,
        handler() {
          this.applyInitialVisibilityPreference();
        },
      },
      canUsePremiumPreference() {
        this.applyInitialVisibilityPreference();
      },
      showPartners() {
        // Footer/viewport updates are handled by onPanelChange with the
        // isAnimating guard — nothing to do here.
      },
      partners() {
        if (this.carouselIndex >= this.partnerSlides.length) {
          this.carouselIndex = 0;
        }
        this.$nextTick(() => {
          this.updatePartnersViewportWidth();
          this.updateFooterHeight();
        });
      },
    },
  };
</script>

<style scoped>
  .partners-carousel {
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
  }

  /* Promote the expanding content to its own compositor layer so the
     panel transition runs on the GPU and isn't interrupted by JS layout work. */
  :deep(.v-expansion-panel-text__wrapper) {
    will-change: contents;
    padding: 0;
  }

  /* Compact header: `static` on the title keeps it from growing when the panel opens */
  :deep(.v-expansion-panel-title) {
    min-height: 44px;
    padding: 10px 16px;
  }

  .partners-title {
    display: inline-flex;
    align-items: center;
    font-size: 0.875rem;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  }

  .partners-viewport {
    width: 100%;
    padding-bottom: 4px;
  }

  /* Compact: thinner title, 56 px logos, arrows on the sides. About 100 px instead of 200 px in all */
  .partners-carousel--compact :deep(.v-expansion-panel-title) {
    min-height: 34px;
    padding: 6px 16px;
  }

  .partners-carousel--compact .partners-viewport {
    position: relative;
    padding: 0 40px 2px;
  }

  .partners-carousel--compact .partners-row {
    height: 64px;
    padding: 4px 6px;
  }

  .carousel-controls--side {
    position: absolute;
    inset: 0;
    margin-top: 0 !important;
    justify-content: space-between !important;
    padding: 0 6px;
    pointer-events: none;
  }

  .carousel-controls--side .carousel-btn {
    pointer-events: auto;
  }

  .carousel-controls--side .carousel-indicator {
    display: none;
  }

  .partners-row {
    width: 100%;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 8px 6px;
  }

  .partner-logo {
    width: 100%;
    height: 100%;
    display: block;
  }

  /* Logos are made for a light background, so the tile stays white in both themes
     and only its border follows the theme. */
  .partner-logo-wrap {
    position: relative;
    width: 100%;
    height: 100%;
    padding: 4px;
    border-radius: 8px;
    background: #fff;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    transition:
      transform 0.15s ease,
      box-shadow 0.15s ease;
  }

  .partner-link:hover .partner-logo-wrap {
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.18);
  }

  .partner-link:focus-visible {
    outline: none;
  }

  .partner-link:focus-visible .partner-logo-wrap {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    .partner-logo-wrap {
      transition: none;
    }

    .partner-link:hover .partner-logo-wrap {
      transform: none;
    }
  }

  .partner-paying-badge {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgb(var(--v-theme-gold));
    display: inline-flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  .partner-slide {
    height: 100%;
  }

  .partner-link {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 var(--partner-slot-width);
    width: var(--partner-slot-width);
    max-width: 100%;
    height: 100%;
  }

  .carousel-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 6px;
  }

  .carousel-btn {
    min-width: 28px;
    width: 28px;
    height: 28px;
  }

  .carousel-indicator {
    font-size: 12px;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    min-width: 44px;
    text-align: center;
  }
</style>
